import { useState } from 'react';
import {
  Modal,
  ModalSettingsShape,
  Input,
  Button,
} from '@rabbit/elements/shared-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { DTConsumer_Private } from '@rabbit/data/types';
import { nestApiChangeConsumerEmail } from '@rabbit/bizproc/core';

export interface ModalChangeEmailProps {
  handleClose: () => void;
  customerDetails: DTConsumer_Private | null;
  modalSettings: ModalSettingsShape;
}

export function ModalChangeEmail({
  handleClose,
  customerDetails,
  modalSettings,
}: ModalChangeEmailProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const schema = Yup.object({
    email: Yup.string()
      .trim()
      .required(t('message.required'))
      .email(t('message.pleaseEnterAValidEmailAddress')),
  });

  const initialValues = {
    email: customerDetails?.email ? customerDetails.email : '',
  };

  return (
    <Modal
      kind="generic"
      settings={modalSettings}
      className="lg:w-[500px]"
      isLoading={isSubmitting}
    >
      <Formik
        onSubmit={async (values) => {
          setIsSubmitting(true);
          try {
            if (!customerDetails?.docid) {
              throw new Error(t('message.customerIdMissing'));
            }

            const result = await nestApiChangeConsumerEmail(
              customerDetails.docid,
              values.email,
              true,
              t('tenantLink')
            );

            if (result.data.success) {
              toast.success(t('message.emailChangedSuccessfully'));
              handleClose();
            } else {
              throw new Error(result.data.message);
            }
          } catch (error: any) {
            const errMsg = error?.response?.data.message || error.message;
            toast.error(
              t(errMsg) || t('message.somethingWentWrongPleaseTryAgain')
            );
          } finally {
            setIsSubmitting(false);
          }
        }}
        initialValues={initialValues}
        validationSchema={schema}
        validateOnChange={true}
        validateOnBlur={false}
      >
        {(props) => (
          <Form className="mt-2 w-full px-5">
            <div className="py-2">
              <Input
                type="email"
                label={t('general.email')}
                name="email"
                settings={{
                  id: 'email',
                  placeholder: t('message.enterNewEmailAddress'),
                  hint: t('message.required'),
                }}
              />
            </div>
            <div className="flex w-full justify-end gap-2 py-2">
              <Button
                loading={isSubmitting}
                kind="primary"
                size="sm"
                onClick={() => {
                  props.handleSubmit();
                }}
                children={t('general.save')}
              />
              <Button
                kind="outline_red"
                size="sm"
                onClick={handleClose}
                children={t('general.cancel')}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
